import {
  ClaimContext,
  ClaimContextFiles,
  ClaimFileType,
  ClaimSubmissionInfo,
  ClaimType,
  ShipmentType,
  SubmitClaimEntryPoint,
} from '../../parcel-finder/claims/types';
import {
  Claim,
  ClaimableShipment,
  ClaimFile,
  ClaimRequirements,
  Shipment,
  ShipmentRecipient,
} from '../../../api/types';
import {
  createEmptyDamageDescription,
  createEmptyInfoItem,
  getCurrencyOfCountry,
} from '../../parcel-finder/claims/claimsHelpers';
import { UploaderFile } from '../../common/Uploader';
import { ClaimShopSettings } from '../../parcel-finder/ClaimsShopSettingsContext';

export function createClaimContextFromShipment(
  entryPoint: SubmitClaimEntryPoint,
  shipment: ClaimableShipment,
  shopSettings: ClaimShopSettings
): ClaimContext {
  return {
    entryPoint,
    shipmentId: shipment.id,
    trackingCode: shipment.trackingCode,
    carrierCode: shipment.carrier.code,
    carrierCountryCode: shipment.carrier.countryCode,
    firstHubScanDate: getDate(shipment.firstHubScanDate),
    ssHubScanDate: getDate(shipment.ssHubScanDate),
    shipmentCreationDate: getDate(shipment.creationDate),
    deliveryDate: getDate(shipment.deliveryDate),
    postalReturnDeliveryDate: getDate(shipment.postalReturnDeliveryDate),
    postalReturnDate: getDate(shipment.postalReturnDate),
    isDelivered: !!shipment.deliveryDate || !!shipment.postalReturnDeliveryDate,
    isReturnShipment: shipment.isReturnShipment,
    orderItems: [],
    deliveryAttemptDate: getDate(shipment.deliveryAttemptDate),
    deliveredToMailboxDate: getDate(shipment.deliveredToMailboxDate),
    weight: shipment.weight || null,
    netValueCurrency: getCurrencyOfCountry(shipment.customer.countryCode),
    partialDamageInfo: [createEmptyDamageDescription()],
    missingItemInfo: [createEmptyInfoItem()],
    customerComment: '',
    recipientName: getRecipientFieldValue(shipment, 'name'),
    recipientAddress: getRecipientFieldValue(shipment, 'address'),
    recipientCity: getRecipientFieldValue(shipment, 'city'),
    recipientZipCode: getRecipientFieldValue(shipment, 'zipCode'),
    recipientCountryCode: getRecipientFieldValue(shipment, 'countryCode'),
    recipientEmail: getRecipientFieldValue(shipment, 'email'),
    recipientPhoneNumber: getRecipientFieldValue(shipment, 'phone'),
    orderId: shipment.order.id,
    shopOrderId: shipment.order.shopOrderId,
    submissionInfo: shipment.claimSubmissionInfo,
    shipmentType: getShipmentType(shipment),
    cashOnDeliveryPaymentType: 'full',
    isTaxIncluded: shopSettings.isDefaultTaxIncluded,
    taxRate: null,
    availableDORLanguages: shipment.availableDORLanguages,
    isOrderItemTaxRate: shopSettings.isOrderItemLevelTaxEnabled,
    consumerActionStatus: shipment.consumerActionStatus || null,
    ...splitClaimFilesIntoClaimContextFiles([]),
    shopSettings,
    claimRequirements: shipment.claimRequirements,
  };
}

export function createClaimContextFromDraft(
  entryPoint: SubmitClaimEntryPoint,
  draft: Claim,
  shopSettings: ClaimShopSettings,
  claimRequirements: ClaimRequirements
): ClaimContext {
  return {
    entryPoint,
    draftId: draft.id,
    type: draft.claimType,
    shipmentId: draft.shipmentId,
    shopOrderId: draft.shopOrderId,
    orderId: null,
    trackingCode: draft.trackingCode,
    carrierCode: draft.carrierCode,
    carrierCountryCode: draft.carrierCountryCode,
    firstHubScanDate: getDate(draft.firstHubScanDate),
    ssHubScanDate: getDate(draft.ssHubScanDate),
    shipmentCreationDate: getDate(draft.shipmentCreationDate),
    deliveryDate: getDate(draft.deliveryDate),
    postalReturnDeliveryDate: getDate(draft.postalReturnDeliveryDate),
    postalReturnDate: getDate(draft.postalReturnDate),
    isDelivered: !!draft.deliveryDate || !!draft.postalReturnDeliveryDate,
    isReturnShipment: draft.isReturnShipment,
    isMultiShipmentOrder: draft.isMultiShipmentOrder,
    deliveryAttemptDate: getDate(draft.deliveryAttemptDate),
    deliveredToMailboxDate: getDate(draft.deliveredToMailboxDate),
    weight: draft.weight || null,
    netValue: draft.netValue / 100,
    netValueCurrency: draft.netValueCurrency,
    partialDamageInfo: [],
    missingItemInfo: [],
    customerComment: draft.customerComment,
    recipientName: draft.recipientName,
    recipientAddress: draft.recipientAddress,
    recipientCity: draft.recipientCity,
    recipientZipCode: draft.recipientZipCode,
    recipientCountryCode: draft.recipientCountryCode,
    recipientEmail: draft.recipientEmail,
    recipientPhoneNumber: draft.recipientPhoneNumber,
    shipmentType: getShipmentType(draft),
    cashOnDeliveryPaymentType: draft.cashOnDeliveryPaymentType,
    orderItems: draft.orderItems.map((item) => ({
      id: `item-${item.id}`,
      orderItemId: item.id,
      description: item.description,
      quantity: item.quantity,
      unitPrice: item.unitPrice ? item.unitPrice / 100 : null,
      taxRate: item.taxRate ? 100 * item.taxRate : null,
    })),
    damageInfo: draft.damageDescription,
    shopClaimId: draft.shopClaimId,
    isTaxIncluded: draft.isTaxIncluded,
    taxRate: draft.taxRate ? 100 * draft.taxRate : null,
    outsideOfDeadlineReason: draft.outsideOfDeadlineReason,
    ...splitClaimFilesIntoClaimContextFiles(draft.files),
    submissionInfo: createClaimSubmissionInfoFromDraft(draft),
    lmcCaseId: draft.lmcCaseId,
    consumerActionStatus: draft.consumerActionStatus || null,
    shopSettings,
    claimRequirements,
  };
}

export function createEmptyClaimContext(
  entryPoint: SubmitClaimEntryPoint,
  shopSettings: ClaimShopSettings
): ClaimContext {
  return {
    entryPoint,
    isDelivered: true, // we pre-select it to avoid user just skipping the delivery date (even if they don't have it)
    partialDamageInfo: [createEmptyDamageDescription()],
    missingItemInfo: [createEmptyInfoItem()],
    weight: 1.0,
    cashOnDeliveryPaymentType: 'full',
    orderItems: [],
    isTaxIncluded: shopSettings.isDefaultTaxIncluded,
    taxRate: null,
    isOrderItemTaxRate: shopSettings.isOrderItemLevelTaxEnabled,
    consumerActionStatus: null,
    ...splitClaimFilesIntoClaimContextFiles([]),
    shopSettings,
    claimRequirements: null,
  };
}

function getDate(strDate: string | null): Date | null {
  return strDate ? new Date(strDate) : null;
}

// for return shipments we want to get info from order recipient
// instead of shipment's customer because for return shipment's the customer is a warehouse
function getRecipientFieldValue(shipment: Shipment, field: keyof ShipmentRecipient): string | null {
  const recipient = shipment.isReturnShipment ? shipment.order.recipient : shipment.customer;
  return recipient[field] || shipment.customer[field];
}

export function createUploaderFileFromClaimFile(claimFile: ClaimFile): UploaderFile {
  return {
    name: claimFile.name,
    size: claimFile.size,
    type: claimFile.mimeType,
    fileType: claimFile.fileType,
    extension: '',
    id: `id-${claimFile.id}`,
    isRemote: true,
    meta: {
      name: claimFile.name,
      type: claimFile.mimeType,
    },
    response: {
      status: 201,
      uploadURL: undefined,
      body: claimFile,
    },
  };
}

function splitClaimFilesIntoClaimContextFiles(files: ClaimFile[]): ClaimContextFiles {
  const declarationOfRecipient = getAllUploaderFilesForClaimFileType(ClaimFileType.DECLARATION_OF_RECIPIENT, files);
  const trackingScreenshot = getAllUploaderFilesForClaimFileType(ClaimFileType.TRACKING_SCREENSHOT, files);
  const invoiceDocuments = getAllUploaderFilesForClaimFileType(ClaimFileType.INVOICE_DOCUMENT, files);
  const picturesOfDamage = getAllUploaderFilesForClaimFileType(ClaimFileType.PICTURE_OF_DAMAGE, files);
  const picturesOfMissingItem = getAllUploaderFilesForClaimFileType(ClaimFileType.PICTURE_OF_MISSING_ITEM, files);
  const proofOfConsumerPayment = getAllUploaderFilesForClaimFileType(ClaimFileType.PROOF_OF_CONSUMER_PAYMENT, files);
  const carrierDamageReports = getAllUploaderFilesForClaimFileType(ClaimFileType.CARRIER_DAMAGE_REPORT, files);
  const deadlineSupportingDocuments = getAllUploaderFilesForClaimFileType(
    ClaimFileType.DEADLINE_SUPPORTING_DOCUMENT,
    files
  );
  const otherDocuments = getAllUploaderFilesForClaimFileType(ClaimFileType.OTHER, files);
  const dataProcessingConsent = getAllUploaderFilesForClaimFileType(ClaimFileType.DATA_PROCESSING_CONSENT, files);

  return {
    declarationOfRecipient,
    trackingScreenshot,
    invoiceDocuments,
    picturesOfDamage,
    picturesOfMissingItem,
    proofOfConsumerPayment,
    carrierDamageReports,
    deadlineSupportingDocuments,
    otherDocuments,
    dataProcessingConsent,
  };
}

function getAllUploaderFilesForClaimFileType(fileType: ClaimFileType, claimFiles: ClaimFile[]): UploaderFile[] {
  return claimFiles.filter((f) => f.fileType === fileType).map(createUploaderFileFromClaimFile);
}

function createClaimSubmissionInfoFromDraft(draft: Claim): ClaimSubmissionInfo {
  return {
    [ClaimType.INVESTIGATION]: { block: null, deadline: null, warning: null },
    [ClaimType.MISSING_ITEM]: { block: null, deadline: null, warning: null },
    [ClaimType.PARTIAL_DAMAGE]: { block: null, deadline: null, warning: null },
    [ClaimType.WHOLE_DAMAGE]: { block: null, deadline: null, warning: null },
    [ClaimType.CASH_ON_DELIVERY]: { block: null, deadline: null, warning: null },
    [draft.claimType]: { block: null, deadline: draft.deadline, warning: draft.claimableWarning },
  };
}

type GetShipmentTypeInput = {
  isReturnShipment: boolean | null;
  postalReturnDate: string | Date | null;
};

function getShipmentType(shipmentOrDraft: GetShipmentTypeInput): ShipmentType {
  if (shipmentOrDraft.isReturnShipment) {
    return ShipmentType.RETURN;
  }

  if (shipmentOrDraft.postalReturnDate) {
    return ShipmentType.UNDELIVERABLE_OR_POSTAL_RETURN;
  }

  return ShipmentType.OUTBOUND;
}
